/**
 * Catalogue Grid - section custom element.
 *
 * Each <catalogue-grid-section> is a Web Component. The browser calls
 * connectedCallback() exactly once per instance when it enters the DOM, so
 * per-section setup (Swiper init, scoped click handler) cannot run twice even
 * if this script is loaded multiple times (e.g. several catalogue sections on
 * the same page).
 *
 * Page-level concerns (close-on-outside-click, close-on-scroll) need to live
 * on document/window. They are bound exactly once via a static guard.
 *
 * The whole declaration is gated by `customElements.get` so re-execution of
 * this script (e.g. multiple sections each emitting the <script> tag) doesn't
 * try to redeclare the class at the global scope (which would throw).
 */
if (!customElements.get('catalogue-grid-section')) {
class CatalogueGridSection extends HTMLElement {
  connectedCallback() {
    this.initSliders();
    this.bindQuickAdd();
    CatalogueGridSection.bindGlobalHandlers();
  }

  initSliders() {
    var sliders = this.querySelectorAll('[data-catalogue-slider]');
    if (sliders.length === 0) return;

    if (typeof Swiper !== 'undefined') {
      sliders.forEach(CatalogueGridSection.initSlider);
      return;
    }
    /* Defensive fallback: Swiper is also defer-loaded so usually ready first, but poll briefly just in case. */
    var checkSwiper = setInterval(function () {
      if (typeof Swiper !== 'undefined') {
        clearInterval(checkSwiper);
        sliders.forEach(CatalogueGridSection.initSlider);
      }
    }, 100);
  }

  static initSlider(slider) {
    if (slider.swiper) return;
    var hasMultipleSlides = slider.querySelectorAll('.swiper-slide').length > 1;
    if (!hasMultipleSlides) return;
    new Swiper(slider, {
      loop: true,
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 400,
      watchSlidesProgress: true,
      navigation: {
        nextEl: slider.querySelector('.swiper-button-next'),
        prevEl: slider.querySelector('.swiper-button-prev'),
      },
      scrollbar: {
        el: slider.querySelector('.swiper-scrollbar'),
        draggable: true,
      },
    });
  }

  /**
   * Quick-add inline sizes — scoped to THIS section.
   * - Click on a size button → write its variant id into the form's hidden input,
   *   then trigger the form submit. <product-form> handles the rest (fetch +
   *   cart drawer open via the theme's force_open_drawer mechanism).
   * - Click on the +/× toggle (mobile) → toggle .catalogue-card--sizes-open.
   * Outside-click and scroll-close are handled in bindGlobalHandlers.
   */
  bindQuickAdd() {
    var section = this;
    this.addEventListener('click', function (e) {
      var sizeBtn = e.target.closest('[data-quick-add-variant]');
      if (sizeBtn) {
        if (sizeBtn.disabled || sizeBtn.getAttribute('aria-busy') === 'true') return;
        var form = sizeBtn.closest('form');
        if (!form) return;
        var idInput = form.querySelector('[data-quick-add-variant-input]');
        var submitBtn = form.querySelector('[data-quick-add-submit]');
        if (!idInput || !submitBtn) return;

        e.preventDefault();
        idInput.value = sizeBtn.getAttribute('data-quick-add-variant');
        sizeBtn.setAttribute('aria-busy', 'true');

        var clear = function () {
          sizeBtn.removeAttribute('aria-busy');
          CatalogueGridSection.closeCard(sizeBtn.closest('.catalogue-card'));
        };
        form.addEventListener('variant:add', clear, { once: true });
        form.addEventListener('cart:error', clear, { once: true });

        submitBtn.click();
        return;
      }

      var toggleBtn = e.target.closest('[data-quick-sizes-toggle]');
      if (toggleBtn) {
        e.preventDefault();
        var card = toggleBtn.closest('.catalogue-card');
        if (!card) return;
        var willOpen = !card.classList.contains('catalogue-card--sizes-open');

        section.querySelectorAll('.catalogue-card--sizes-open').forEach(function (other) {
          if (other !== card) CatalogueGridSection.closeCard(other);
        });

        card.classList.toggle('catalogue-card--sizes-open', willOpen);
        toggleBtn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      }
    });
  }

  static closeCard(card) {
    if (!card) return;
    card.classList.remove('catalogue-card--sizes-open');
    var toggle = card.querySelector('[data-quick-sizes-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  static bindGlobalHandlers() {
    if (CatalogueGridSection._globalsBound) return;
    CatalogueGridSection._globalsBound = true;

    /* Close any open card on click outside. Per-section listener handles size + toggle clicks; this only fires for clicks that didn't hit those. */
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-quick-add-variant]')) return;
      if (e.target.closest('[data-quick-sizes-toggle]')) return;
      if (e.target.closest('.catalogue-card--sizes-open')) return;
      document.querySelectorAll('.catalogue-card--sizes-open').forEach(CatalogueGridSection.closeCard);
    });

    /* Close on scroll, deferred to next animation frame so the CSS transition runs (browsers throttle transitions while a scroll handler is executing). */
    window.addEventListener('scroll', function () {
      var openCards = document.querySelectorAll('.catalogue-card--sizes-open');
      if (openCards.length === 0) return;
      requestAnimationFrame(function () {
        openCards.forEach(CatalogueGridSection.closeCard);
      });
    }, { passive: true });
  }
}

customElements.define('catalogue-grid-section', CatalogueGridSection);
}

/**
 * Catalogue Grid Mode Switch - grid column switcher in the facets toolbar.
 *
 * Mirrors the native <collection-layout-switch> pattern (theme.js:4434):
 *   - Updates the CSS variable on the parent section instantly (0ms perceived)
 *   - Persists the choice via POST /cart/update.js with keepalive (background)
 *
 * Two instances per section (device="mobile" with 2 buttons, device="desktop"
 * with 4 buttons). Each button's `value` is the column count (string).
 *
 * The component finds its parent <catalogue-grid-section> by data-section-id
 * and writes either --catalogue-columns-mobile or --catalogue-columns on it,
 * matching the CSS variables consumed in section-catalogue-grid.css.
 *
 * Gated by customElements.get for idempotent re-execution (matches the pattern
 * used for CatalogueGridSection above).
 */
if (!customElements.get('catalogue-grid-mode-switch')) {
class CatalogueGridModeSwitch extends HTMLElement {
  connectedCallback() {
    this._onClick = this.onButtonClick.bind(this);
    this.addEventListener('click', this._onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
  }

  onButtonClick(event) {
    var button = event.target.closest('button[value]');
    if (!button || button.classList.contains('is-active')) return;

    var value = button.value;
    var device = this.getAttribute('device');
    var attributeName = this.getAttribute('data-attribute-name');
    var section = this.closest('catalogue-grid-section') || document.getElementById('shopify-section-' + this.getAttribute('data-section-id'));
    if (!section) return;

    /* Instant CSS update so the grid reflows immediately. The desktop var
       keeps the historical name `--catalogue-columns` for backwards-compat
       (used in .catalogue-card width calc); mobile and tablet use
       `--catalogue-columns-mobile` and `--catalogue-columns-tablet`
       respectively, each consumed in its own media query. */
    var cssVar = '--catalogue-columns';
    if (device === 'mobile') cssVar = '--catalogue-columns-mobile';
    else if (device === 'tablet') cssVar = '--catalogue-columns-tablet';
    section.style.setProperty(cssVar, value);

    /* Toggle active state across siblings in THIS switch only. */
    Array.from(this.querySelectorAll('button')).forEach(function (btn) {
      btn.classList.toggle('is-active', btn === button);
    });

    /* Persist cross-page via cart attributes — fire-and-forget. keepalive
       ensures the request completes even if the user navigates away. */
    if (attributeName) {
      var body = { attributes: {} };
      body.attributes[attributeName] = value;
      fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        keepalive: true
      });
    }
  }
}
customElements.define('catalogue-grid-mode-switch', CatalogueGridModeSwitch);
}

